import { Fragment, useState } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { GoChevronDown, GoX } from "react-icons/go";
import Header from "../../Common/header";
import Footer from "../../Common/footer";
import FilterSection from "./filterSection.js";
import SearchBarSection from "./searchBarSection.js";
import Pagination from "./pagination/pagination.js";
import useDocumentTitle from "../../Common/useDocumentTitle";
import { ImSpinner8 } from "react-icons/im";

export default function DataCatalogue(props) {
  useDocumentTitle("Data Catalogue");
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    coordinates: {
      eastLongitude: urlParams.get("eastLong") || "",
      northLatitude: urlParams.get("northLat") || "",
      southLatitude: urlParams.get("southLat") || "",
      westLongitude: urlParams.get("westLong") || "",
    },
    keywords: urlParams.get("keywords")?.split(",") || [],
    researchPlatform: urlParams.get("researchPlatform")?.split(",") || [],
    publicationYear: urlParams.get("publicationYear")?.split(",") || "",
    duration: {
      start: urlParams.get("startDate") || "",
      end: urlParams.get("endDate") || "",
    },
    publisher: urlParams.get("publisher")?.split(",") || [],
  });
  const [projectCount, setProjectCount] = useState(0); //Used for displaying how many projects were found from API call
  const [projects, setProjects] = useState(null);
  const [activeSortOption, setActiveSortOption] = useState({
    field: "title",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);

  /**
   * Truncates a string to a specified length to the nearest word.
   *
   * @param {*} str String to truncate.
   * @param {*} newLength Amount of characters to truncate to.
   * @returns Truncated string.
   */
  function truncateString(str, newLength) {
    if (str.length <= newLength) {
      return str;
    }
    const subString = str.slice(0, newLength - 1);
    return subString.slice(0, subString.lastIndexOf(" ")) + "\u2026";
  }

  return (
    <>
      <Header />
      <div className="bg-white dark:bg-gray-900">
        <div>
          {/* Mobile filter dialog ---------------------------------------------------------------------*/}
          <Transition.Root show={mobileFiltersOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-40 lg:hidden"
              onClose={setMobileFiltersOpen}
            >
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black bg-opacity-25" />
              </Transition.Child>
              <div className="fixed inset-0 flex z-40">
                <Transition.Child
                  as={Fragment}
                  enter="transition ease-in-out duration-300 transform"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transition ease-in-out duration-300 transform"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="ml-auto relative max-w-xs w-full h-full bg-white shadow-xl py-4 pb-12 flex flex-col overflow-y-auto dark:bg-gray-800">
                    <div className="px-4 flex items-center justify-between">
                      <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                        Filters
                      </h2>
                      <button
                        type="button"
                        className="-mr-2 w-10 h-10 bg-white p-2 rounded-md flex items-center justify-center text-gray-400 dark:bg-gray-800 dark:hover:bg-gray-700"
                        onClick={() => setMobileFiltersOpen(false)}
                      >
                        <span className="sr-only"> Close menu </span>
                        <GoX className="h-6 w-6" />
                      </button>
                    </div>
                    {/* Mobile Filters */}
                    <form className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
                      <FilterSection
                        filters={filters}
                        setFilters={setFilters}
                      />
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition.Root>
          {/* Mobile filter dialog ends ---------------------------------------------------------------------*/}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative z-10 flex items-baseline justify-between pt-8 lg:pt-12 pb-2 border-b border-gray-300 dark:border-gray-700">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 hidden lg:block dark:text-white">
                Filters
              </h1>
              {/* Search bar and mobile filters button ---------------------------------------------------------------------*/}
              <SearchBarSection
                setProjects={setProjects}
                filters={filters}
                setProjectCount={setProjectCount}
                activeSortOption={activeSortOption}
                setMobileFiltersOpen={setMobileFiltersOpen}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                homeBarSearchValue={props.location.state}
                setLoading={setLoading}
              />
            </div>
            <section className="pt-4 pb-24">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-8 gap-y-10">
                {/* Desktop Filters -------------------------------------------------------------------*/}
                <form className="hidden lg:block">
                  <FilterSection filters={filters} setFilters={setFilters} />
                </form>
                {/* Projects grid ---------------------------------------------------------------------*/}
                <div className="lg:col-span-3">
                  <div className="relative flex mb-4 items-center py-0">
                    <p className="min-w-0 flex-1 font-medium text-gray-900 text-sm dark:text-white">
                      {projectCount} Dataset{projectCount === 1 ? "" : "s"} Found
                    </p>
                    {/* Sort button -------------------------------------------------------------------*/}
                    <Menu as="div" className="relative inline-block text-right">
                      <Menu.Button className="group inline-flex justify-center text-sm font-medium text-gray-900 dark:text-white">
                        Sort By: {activeSortOption.field === "title" ? "Alphabetical" : "Recently Added"}
                        <GoChevronDown className="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="z-40 origin-top-right absolute border border-gray-200 right-0 mt-2 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:border-gray-700">
                          <div className="py-1 ">
                            <>
                              <Menu.Item>
                                <button
                                  className="w-full"
                                  onClick={() => {
                                    setActiveSortOption({
                                      field: "publishedAt",
                                      direction: "desc",
                                    });
                                  }}
                                >
                                  {activeSortOption.field === "publishedAt" ? (
                                    <p className="text-right mb-0 bg-gray-100 text-gray-900 font-medium block px-4 py-2 text-sm dark:bg-gray-700 dark:text-white">
                                      Recently Added
                                    </p>
                                  ) : (
                                    <p className="text-right mb-0 text-gray-700 hover:bg-gray-100 block px-4 py-2 text-sm dark:text-gray-400 dark:hover:bg-gray-700">
                                      Recently Added
                                    </p>
                                  )}
                                </button>
                              </Menu.Item>
                              <Menu.Item>
                                <button
                                  className="w-full"
                                  onClick={() => {
                                    setActiveSortOption({
                                      field: "title",
                                      direction: "asc",
                                    });
                                  }}
                                >
                                  {activeSortOption.field === "title" ? (
                                    <p className="text-right mb-0 bg-gray-100 text-gray-900 font-medium block px-4 py-2 text-sm dark:bg-gray-700 dark:text-white">
                                      Alphabetical
                                    </p>
                                  ) : (
                                    <p className="text-right mb-0 text-gray-700 hover:bg-gray-100 block px-4 py-2 text-sm dark:text-gray-400 dark:hover:bg-gray-700">
                                      Alphabetical
                                    </p>
                                  )}
                                </button>
                              </Menu.Item>
                            </>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>
                  {loading &&
                    <div className="w-full flex justify-center mb-4">
                      <ImSpinner8 className="w-10 h-10 text-gray-300 animate-spin" />
                    </div>
                  }
                  {/* Projects go here ---------------------------------------- */}
                  {/* 'projects' contains all the projects to display and is set in 'searchBarSection' */}
                  {projects !== null ? (
                    projects.map((project) => (
                      <a
                        key={project.id || project.PK}
                        href={"/metadata-display/" + project.id}
                      >
                        <div
                          className="bg-white hover:shadow-md relative block p-8 overflow-hidden border border-gray-200 rounded-xl shadow mb-5 transform translate-x-0 hover:translate-x-4 duration-500 ease-in-out dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
                        >
                          <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-blue-500 to-blue-600"></span>
                          <div className="justify-between sm:flex">
                            <div>
                              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {project.title}
                              </h2>
                            </div>

                            {/* <div className="flex-shrink-0 hidden ml-3 sm:block">
                          </div> */}
                          </div>
                          <div className="mt-4 sm:pr-8">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {/* Shorten project abstract to 230 characters to nearest word*/}
                              {truncateString(project.abstract, 230)}
                            </p>
                          </div>
                          <dl className="flex mt-6">
                            <div className="flex flex-col-reverse">
                              <dd className="text-xs text-gray-500 dark:text-gray-400">
                                {project.publishedAt ? new Date(project.publishedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : 'N/A'}
                              </dd>
                              <dt className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                Published
                              </dt>
                            </div>

                            <div className="flex flex-col-reverse ml-3 sm:ml-6">
                              <dd className="text-xs text-gray-500 dark:text-gray-400">
                                {JSON.parse(project.stakeholders)[0].name}
                              </dd>
                              <dt className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                Published By
                              </dt>
                            </div>
                          </dl>
                        </div>
                      </a>
                    ))
                  ) : (
                    <p></p>
                  )}
                  {/* End Projects ---------------------------------------- */}

                  {/* Pagination */}
                  <Pagination
                    projectsPerPage={6}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    projectCount={projectCount}
                  />
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}